import { useState, useEffect, useContext } from "react";
import { ImageContext } from "../Component/Context/ImageContext";
import { applyPerspectiveWarp } from "../utils/newPerspectiveWarp";
import { generateHighResImage, downloadImage } from "../utils/imageGenerator";
import { generateGliderolFilename } from "../utils/fileNaming";

export const useMobileImageHandling = (points, image, bgImage) => {
  const { setImgurl } = useContext(ImageContext);
  const [houseImageDimensions, setHouseImageDimensions] = useState({
    width: 370,
    height: 300,
  });

  // Generate live preview image (low resolution for performance)
  const handleGenerateImage = async () => {
    if (!bgImage || !image || points.length !== 4) return;

    const canvas = document.createElement("canvas");
    canvas.width = 370;
    canvas.height = 300;
    const ctx = canvas.getContext("2d");

    const bg = new Image();
    bg.crossOrigin = "anonymous";
    bg.src = bgImage;

    const door = new Image();
    door.crossOrigin = "anonymous";
    door.src = image;

    await Promise.all([
      new Promise((res) => (bg.onload = res)),
      new Promise((res) => (door.onload = res)),
    ]);

    // Draw background
    ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);

    // Apply perspective warp with the exact points from dragging
    applyPerspectiveWarp({
      ctx,
      img: door,
      from: [
        { x: 0, y: 0 },
        { x: door.naturalWidth, y: 0 },
        { x: door.naturalWidth, y: door.naturalHeight },
        { x: 0, y: door.naturalHeight },
      ],
      to: points, // Use points directly without scaling for live preview
    });
    
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setImgurl(url);
    }, "image/png");
  };

  // Save image using shared high-resolution generator
  const handleSaveImage = async () => {
    if (!bgImage || !image || points.length !== 4) return;

    try {
      // Get actual container dimensions
      const containerElement = document.querySelector('#image-display-area');
      let actualContainerDimensions = { width: 370, height: 300 }; // fallback
      
      if (containerElement) {
        const rect = containerElement.getBoundingClientRect();
        actualContainerDimensions = {
          width: rect.width,
          height: rect.height
        };
        console.log('Mobile save - actual container dimensions:', actualContainerDimensions);
      }

      // Generate high-resolution image using shared utility
      const dataUrl = await generateHighResImage({
        bgImage,
        doorImage: image,
        points,
        houseImageDimensions,
        containerDimensions: actualContainerDimensions, // Use actual container size
        targetWidth: 1200,
        format: 'jpeg',
        quality: 0.95
      });

      // Download the image
      downloadImage(dataUrl, generateGliderolFilename());
      
    } catch (error) {
      console.error("Error generating high-res image:", error);
      alert("Failed to generate image. Please try again.");
    }
  };

  // Generate high-resolution image for email (without downloading)
  const generateImageForEmail = async () => {
    if (!bgImage || !image || points.length !== 4) return null;

    try {
      // Get actual container dimensions
      const containerElement = document.querySelector('#image-display-area');
      let actualContainerDimensions = { width: 370, height: 300 }; // fallback
      
      if (containerElement) {
        const rect = containerElement.getBoundingClientRect();
        actualContainerDimensions = {
          width: rect.width,
          height: rect.height
        };
      }

      return await generateHighResImage({
        bgImage,
        doorImage: image,
        points,
        houseImageDimensions,
        containerDimensions: actualContainerDimensions, // Use actual container size
        targetWidth: 1200,
        format: 'jpeg',
        quality: 0.95
      });
    } catch (error) {
      console.error("Error generating image for email:", error);
      return null;
    }
  };

  // Auto-generate preview image when dependencies change
  useEffect(() => {
    if (!bgImage || !image || points.length !== 4) return;

    const timeout = setTimeout(() => {
      handleGenerateImage();
    }, 100);

    return () => clearTimeout(timeout);
  }, [bgImage, image, points]);

  return {
    houseImageDimensions,
    setHouseImageDimensions,
    handleGenerateImage,
    handleSaveImage,
    generateImageForEmail, // New function for email attachment
  };
};